import { HTMLProps, useEffect, useRef, useState } from "react";

import { DateTime } from "luxon";

import { createColumnHelper, RowModel } from "@tanstack/react-table";
import { CheckCircleIcon, ChevronRightIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import BulkListModal from "./BulkListModal";
import PartImage from "../../stock/PartImage";
import PopOutBox from "../../shared/PopOutBox";
import ClickToCopy from "../../shared/ClickToCopy";
import UnlistedNpmUpdate from "./UnlistedMpnUpdate";
import { IPartCategory } from '../../../models/Part';
import { PageMode } from "../../../models/PagedTable";
import UnlistedOfferPrice from "./UnlistedOfferPrice";
import openInNewTab from "../../../utils/openInNewTab";
import PagedTable from "../../shared/Tables/PagedTable";
import UnlistedGradeUpdate from "./UnlistedGradeUpdate";
import UnlistedReadyStatus from "./UnlistedReadyStatus";
import UnlistedKTypeUpdate from "./UnlistedKTypeUpdate";
import UnlistedCategoryRow from "./UnlistedCategoryRow";
import { IPart, IPartImageType } from "../../../models/Part";
import formatDateTime from './../../../utils/formatDateTime';
import usePartMutations from "../../../data/usePartMutations";
import PartStatusLabel from "../../ebayOrders/PartStatusLabel";
import useNotification from "../../notifications/useNotifications";
import UnlistedExpandedRowContent from "./UnlistedExpandedRowContent";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import ChangeCategoryModal from "../../stock/stockFunctions/ChangeCatgeoryModal";
import WarehouseLocationFromId from "../../warehouseLocations/WarehouseLocationFromId";
import useTemplateProductDescription from "../../../data/useTemplateProductDescription";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";


function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer'}
      {...rest}
    />
  )
}

export default function EbayUnlistedTable({ categories }: { categories: IPartCategory[] | undefined; }) {
  const { autoReorderImages } = usePartMutations();
  const { useBulkDefaultPartCategory } = usePartDefaultCategoriesMutations();
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { saveHandlers } = useDefaultCRUDHandlers("Part");
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { addNotification } = useNotification();

  const [bulkListLoading, setBulkListLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(50);
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState<boolean>(false);
  const [bulkListModalOpen, setBulkListModalOpen] = useState<boolean>(false);
  const [selectedPartsForBulkList, setSelectedPartsForBulkList] = useState<IPart[] | undefined>(undefined);


  const colHelper = createColumnHelper<IPart>();

  const cols = [
    {
      id: 'select',
      header: ({ table }: any) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }: any) => (
        <div className="flex justify-center items-center flex-1 p-1" onClick={(e) => e.stopPropagation()}>
          <IndeterminateCheckbox
            {...{
              id: row.original.id,
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
          <button
            onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
            style={{ transform: `rotate(${!row.getIsExpanded() ? "0" : "-270"}deg)` }}
            className="ml-3 p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
          >
            {/* {row.getIsExpanded() ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4"/>} */}
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    colHelper.display({
      id: "images",
      cell: (info) => <PartImage part={info.row.original} width="min-w-10 max-w-32" />
    }),
    colHelper.accessor("categoryId", {
      header: "Category",
      id: "categoryId",
      sortDescFirst: false,
      sortingFn: "auto",
      cell: (info) => <UnlistedCategoryRow part={info.row.original} categories={categories} />,
    }),
    colHelper.accessor("tagNumber", {
      header: "Tag No.",
      id: "tagNumber",
      sortingFn: "auto",
      cell: (info) => <div className="flex flex-col">
        <ClickToCopy text={info.row.original.tagNumber} />
      </div>,
    }),
    colHelper.accessor("vehicle.vrm", {
      header: "Vehicle",
      id: "vrm",
      sortingFn: "auto",
      cell: (info) => <div className="flex flex-col">
        {/* <span>{info.row.original.vehicle?.vrm ?? ""}</span> */}
        <ClickToCopy text={info.row.original.vehicle?.vrm} />
        <span>{info.row.original.vehicle?.combinedMake ?? ""}</span>
        <span>{info.row.original.vehicle?.combinedModel ?? ""}</span>
        <ClickToCopy text={info.row.original.vehicle?.combinedVIN} textClassName="text-xs" />
        <UnlistedKTypeUpdate part={info.row.original} />
      </div>,
    }),
    colHelper.accessor(row => {
      const offer = row.offers[0];
      return offer ? ((offer.auctionStartPrice ?? 0) + (offer.buyItNowPrice ?? 0)) : 0;
    }, {
      id: "offer price",
      header: "Price",
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        //Sort by value of both
        return (parseFloat(rowA.original.offers[0]?.auctionStartPrice ?? 0)) + (parseFloat(rowA.original.offers[0]?.buyItNowPrice ?? 0)) -
          (parseFloat(rowB.original.offers[0]?.auctionStartPrice ?? 0)) - (parseFloat(rowB.original.offers[0]?.buyItNowPrice ?? 0));
      },
      cell: (info) => <UnlistedOfferPrice part={info.row.original} />
    }),
    // colHelper.accessor("offers", {
    //   id: "error state",
    //   header: "Error",
    //   sortingFn: "auto",
    //   cell: (info) => <TableListingError part={info.row.original} />
    // }),
    colHelper.accessor(row => row.images.some(img => img.imageType === IPartImageType.Damage), {
      header: "Damage",
      cell: (info) => {
        const hasDamage = info.getValue();
        const damageImages = info.row.original.images.filter(img => img.imageType === IPartImageType.Damage);

        return (
          <span className="flex justify-center text-xl">
            {hasDamage ? (
              <PopOutBox
                classes="max-w-3xl w-[40vw] max-h-[50vh] overflow-y-auto"
                icon={<ExclamationTriangleIcon className="h-6 w-6 text-red-500" />}
              >
                <div className="p-2">
                  <p className="text-sm font-medium mb-2">Damage Images:</p>
                  <div className="space-y-2">
                    {damageImages.map((image, index) => (
                      <img
                        key={index}
                        src={image.url}
                        alt={`Damage ${index + 1}`}
                        className="w-full h-auto rounded border"
                        style={{ maxHeight: '400px', objectFit: 'contain' }}
                      />
                    ))}
                  </div>
                </div>
              </PopOutBox>
            ) : (
              <CheckCircleIcon className="h-6 w-6 text-green-500" />
            )}
          </span>
        );
      },
    }),
    colHelper.display({
      id: "mpn image",
      header: "MPN",
      cell: (info) => <UnlistedNpmUpdate part={info.row.original} />,
    }),
    colHelper.accessor("warehouseLocationId", {
      header: "Warehouse",
      sortingFn: "auto",
      cell: (info) => <WarehouseLocationFromId id={info.getValue()} lastStockChecked={info.row.original.lastStockChecked} />,
    }),
    colHelper.accessor("grade", {
      header: "Grade",
      cell: (info) => <UnlistedGradeUpdate part={info.row.original} />,
    }),
    colHelper.accessor("vehicle", {
      id: "mileage",
      header: "Mileage",
      cell: (info) => <span className="flex justify-center">{info.getValue()?.odometerInMiles?.toLocaleString() ?? "N/A"}</span>,
    }),
    colHelper.accessor("created", {
      id: "created",
      header: "Created",
      cell: (info) => <span className="flex justify-center">{formatDateTime(info.getValue(), DateTime.DATE_SHORT)}</span>,
    }),
    // colHelper.accessor("offers", {
    //   id: "offer status",
    //   header: "Offer Status",
    //   enableSorting: false,
    //   cell: (info) => <ListingStatus offer={info.getValue()?.at(0)} part={info.row.original} />,
    // }),
    colHelper.accessor("partStatus", {
      header: "Part Status",
      enableSorting: false,
      cell: (info) => <PartStatusLabel part={info.row.original} />,
    }),
    colHelper.display({
      header: "Ready To List",
      enableSorting: false,
      cell: (info) => <UnlistedReadyStatus part={info.row.original} />,
    }),
  ];



  const bulkList = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    setSelectedPartsForBulkList(rows.rows.map((row) => row.original));
    setBulkListModalOpen(true);
  };

  //Not used anymore
  // const handleBulkListConfirm = (partIds: string[]) => {
  //   if (partIds.length === 0) {
  //     addNotification({
  //       primaryText: "No parts selected for listing",
  //       variant: "warn"
  //     });
  //     return;
  //   }

  //   setBulkListLoading(true);

  //   // TODO: Replace with your new bulk list endpoint
  //   // Example:
  //   // yourNewBulkListEndpoint.mutate({ partIds }, {
  //   //   onSuccess: (data) => {
  //   //     console.log(data);
  //   //     // Handle success - maybe open a new tab or show success message
  //   //     setBulkListModalOpen(false);
  //   //   },
  //   //   onError: () => {
  //   //     saveHandlers.onError();
  //   //   },
  //   //   onSettled: () => {
  //   //     setBulkListLoading(false);
  //   //   }
  //   // });

  //   // For now, just close the modal and show a placeholder message
  //   setTimeout(() => {
  //     addNotification({
  //       primaryText: `${partIds.length} parts selected for bulk listing`,
  //       variant: "success"
  //     });
  //     setBulkListModalOpen(false);
  //     setBulkListLoading(false);
  //   }, 1000);
  // };

  const handleAutoReorderImages = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const ids = selected.map((row) => row.original.id);

    autoReorderImages.mutate(ids, bulkSaveHandlers);
  }

  const handleApplyBulkDefaults = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const body = {
      partIds: selected.map((row) => row.original.id)
    };

    useBulkDefaultPartCategory.mutate(body as any, bulkSaveHandlers);
  };


  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    bulkUpdateProductDescription.mutate(partIds, bulkSaveHandlers);
  };


  const handleOpenChangeCategory = (rows: RowModel<IPart>) => {
    setToUpdate(rows);
    setUpdateCategoryOpen(true);
  };

  const expandedRow = (part: IPart) => {
    return <UnlistedExpandedRowContent part={part} />;
  };

  return (
    <>
      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      {selectedPartsForBulkList && (
        <BulkListModal
          open={bulkListModalOpen}
          setOpen={setBulkListModalOpen}
          selectedParts={selectedPartsForBulkList}
        />
      )}
      <PagedTable
        url="/part/listings"
        pageMode={PageMode.Unlisted}
        columns={cols}
        queryLabel="Search part details"
        // customUrlQuery={`pageSize=${pageSize}`}
        customUrlQuery=""
        onRowClick={(x: IPart) => openInNewTab(`ebay-listings/${x.id}`)}
        actionButtons={[
          { label: "Bulk List", onClick: bulkList, isLoading: bulkListLoading, disabledOnNoRows: true },
          { label: "Auto Reorder Images", onClick: handleAutoReorderImages, isLoading: autoReorderImages.isLoading, disabledOnNoRows: true },
          { label: "Apply Defaults", onClick: handleApplyBulkDefaults, isLoading: useBulkDefaultPartCategory.isLoading, disabledOnNoRows: true },
          { label: "Reset Description", onClick: handleBulkResetDescription, isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true },
          { label: "Change Category", onClick: handleOpenChangeCategory, disabledOnNoRows: true },

        ]}
        expandedRow={expandedRow}
        showTotal
        modeToggles={[
          { label: "Missing/Deleted", pageMode: PageMode.UnlistedMissingDeleted },
          { label: "QC", pageMode: PageMode.UnlistedQC },
          { label: "Dismantled", pageMode: PageMode.UnlistedDismantled },
          { label: "Located", pageMode: PageMode.UnlistedLocated },
          { label: "No Images", pageMode: PageMode.UnlistedNoImages },
          { label: "No Price", pageMode: PageMode.UnlistedNoPrice },
          { label: "No MPN", pageMode: PageMode.UnlistedNoMpn },
        ]}
      />
      {/* <div className="flex gap-x-2 items-center mt-4">
        <label>Page Size:</label>
        <input className="w-20" type="number" value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value))} />
      </div> */}
    </>
  );
}
